import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BlogSchemas, type PopulatedBlog } from "@workspace/contracts/blogs";
import { Button } from "@workspace/ui/components/button";
import { useState } from "react";
import { orpc } from "@/api/orpc";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { DeleteBlog } from "@/features/blogs/actions/delete";
import { useBlogsColumns } from "@/features/blogs/hooks/use-blogs-columns";
import { useDataTable } from "@/hooks/use-data-table";
import { Main } from "@/layout/main";
import { withDashboardPaginationDefaults } from "@/lib/pagination";
import type { DataTableRowAction } from "@/types/data-table";

export const Route = createFileRoute("/_authenticated/blogs/")({
  validateSearch: (search) => BlogSchemas.list.request.parse(withDashboardPaginationDefaults(search)),
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { orpc, queryClient } }) => {
    const blogs = await queryClient.ensureQueryData(orpc.admin.blogs.list.queryOptions({ input: deps }));
    return { blogs };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const search = Route.useSearch();

  const [rowAction, setRowAction] = useState<DataTableRowAction<PopulatedBlog> | null>(null);

  const columns = useBlogsColumns({ setRowAction });

  const { data: blogs } = useSuspenseQuery(orpc.admin.blogs.list.queryOptions({ input: search }));

  const { table } = useDataTable({
    columns: columns,
    data: blogs.items,
    pageCount: blogs.totalPageNumbers,
    debounceMs: 500,
    getRowId: (originalRow) => originalRow.id,
    enableSorting: true,
    initialState: { columnVisibility: { slug: false } },
  });

  return (
    <>
      <Main>
        <DataTable table={table}>
          <DataTableToolbar table={table}>
            <Button size="sm" variant="outline" asChild>
              <Link to="/blogs/new">+ Create New Blogs</Link>
            </Button>
          </DataTableToolbar>
        </DataTable>
      </Main>

      <DeleteBlog
        blog={rowAction?.row.original}
        open={rowAction?.variant === "delete"}
        onClose={() => setRowAction(null)}
      />
    </>
  );
}
